import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Footer from "../../../customComponents/Footer";
import "./straight.css";

function StraightHeroParticles() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId;
    let width = (canvas.width = canvas.parentElement?.offsetWidth || window.innerWidth);
    let height = (canvas.height = canvas.parentElement?.offsetHeight || 1060);

    const colors = [
      "rgba(245, 184, 66,",   // Radiant Gold
      "rgba(255, 217, 120,",  // Champagne Sparkle
      "rgba(230, 167, 58,",   // Warm Amber
      "rgba(255, 241, 197,",  // Ultra Shimmer Gold
      "rgba(209, 170, 103,",  // AGCX Signature Gold
    ];

    let particles = [];

    const createParticles = () => {
      const isMobile = width < 768;
      const count = isMobile ? 50 : 110;

      particles = Array.from({ length: count }, () => {
        const radius = isMobile
          ? Math.random() * 1.6 + 0.9
          : Math.random() * 2.2 + 1.1;

        // Slower and gentle on mobile, fast and lively on desktop
        const speedY = isMobile
          ? Math.random() * 0.42 + 0.28
          : Math.random() * 1.2 + 0.75;

        const speedX = isMobile
          ? (Math.random() - 0.5) * 0.2
          : (Math.random() - 0.5) * 0.4;

        const swaySpeed = isMobile
          ? Math.random() * 0.02 + 0.01
          : Math.random() * 0.035 + 0.015;

        const swayRange = isMobile
          ? Math.random() * 1.0 + 0.4
          : Math.random() * 1.8 + 0.8;

        const pulseSpeed = isMobile
          ? Math.random() * 0.025 + 0.012
          : Math.random() * 0.045 + 0.02;

        return {
          x: Math.random() * width,
          y: Math.random() * height,
          radius,
          speedY,
          speedX,
          swaySpeed,
          swayRange,
          pulsePhase: Math.random() * Math.PI * 2,
          pulseSpeed,
          phase: Math.random() * Math.PI * 2,
          baseColor: colors[Math.floor(Math.random() * colors.length)],
          baseOpacity: isMobile
            ? Math.random() * 0.4 + 0.4
            : Math.random() * 0.45 + 0.45,
        };
      });
    };

    createParticles();

    const handleResize = () => {
      if (!canvas || !canvas.parentElement) return;
      const prevWidth = width;
      width = canvas.width = canvas.parentElement.offsetWidth;
      height = canvas.height = canvas.parentElement.offsetHeight;

      // Re-initialize particles if cross breakpoint between desktop and mobile
      const wasMobile = prevWidth < 768;
      const nowMobile = width < 768;
      if (wasMobile !== nowMobile || Math.abs(prevWidth - width) > 150) {
        createParticles();
      }
    };

    window.addEventListener("resize", handleResize);

    const render = () => {
      ctx.clearRect(0, 0, width, height);

      particles.forEach((p) => {
        p.phase += p.swaySpeed;
        p.pulsePhase += p.pulseSpeed;

        p.x += Math.sin(p.phase) * p.swayRange * 0.4 + p.speedX;
        p.y += p.speedY;

        // Wrap vertically across full canvas
        if (p.y > height + 20) {
          p.y = -15;
          p.x = Math.random() * width;
        } else if (p.y < -20) {
          p.y = height + 15;
          p.x = Math.random() * width;
        }

        // Wrap horizontally
        if (p.x < -20) p.x = width + 15;
        if (p.x > width + 20) p.x = -15;

        // Dynamic twinkle shimmer
        const pulse = 0.65 + 0.35 * Math.sin(p.pulsePhase);
        const opacity = Math.min(1, Math.max(0.12, p.baseOpacity * pulse));

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius * (0.85 + 0.25 * pulse), 0, Math.PI * 2);
        ctx.fillStyle = `${p.baseColor}${opacity.toFixed(3)})`;
        ctx.shadowBlur = p.radius * 4.5;
        ctx.shadowColor = `${p.baseColor}${(opacity * 0.9).toFixed(3)})`;
        ctx.fill();
      });

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return <canvas ref={canvasRef} className="straight-hero-particles-canvas" />;
}

function StepsGoldenMatrix({ side = "left" }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId;
    let width = (canvas.width = canvas.parentElement?.offsetWidth || 300);
    let height = (canvas.height = canvas.parentElement?.offsetHeight || 640);

    const handleResize = () => {
      if (!canvas || !canvas.parentElement) return;
      width = canvas.width = canvas.parentElement.offsetWidth;
      height = canvas.height = canvas.parentElement.offsetHeight;
      initGrid();
    };

    window.addEventListener("resize", handleResize);

    const cellSize = 6.8;
    const gap = 3.8;
    const pitch = cellSize + gap;

    // Rich luxury multi-tonal gold spectrum with warm amber, bright champagne, and radiant sunset gold
    const colorSpectrum = [
      { r: 209, g: 170, b: 103 }, // #D1AA67 AGCX Gold
      { r: 245, g: 184, b: 66 },  // #F5B842 Radiant Gold
      { r: 255, g: 215, b: 100 }, // #FFD764 Sunshine Amber
      { r: 255, g: 240, b: 185 }, // #FFF0B9 Ultra Sparkle Champagne
      { r: 235, g: 155, b: 45 },  // #EB9B2D Deep Amber
      { r: 195, g: 145, b: 70 },  // #C39146 Satin Bronze
    ];

    let pixels = [];

    const initGrid = () => {
      pixels = [];
      const currentCols = Math.floor(width / pitch);
      const currentRows = Math.floor(height / pitch);

      for (let c = 0; c < currentCols; c++) {
        for (let r = 0; r < currentRows; r++) {
          const x = c * pitch;
          const y = r * pitch;

          const normX = side === "left" ? c / currentCols : (currentCols - 1 - c) / currentCols;
          const normY = r / currentRows;

          // Organic convex taper: widest near the center height
          const vBell = Math.sin(Math.PI * Math.max(0, Math.min(1, normY)));
          const horizontalReach = 0.38 + 0.62 * vBell;
          const normXAdjusted = normX / horizontalReach;

          if (normXAdjusted > 1) continue;

          // Dense at edge, gradual organic fade inward
          const hProb = Math.pow(Math.max(0, 1 - normXAdjusted), 1.2) * 0.98;
          const vFade = Math.pow(vBell, 0.35);
          const prob = hProb * vFade;

          if (Math.random() < prob) {
            const baseAlpha = (Math.pow(Math.max(0, 1 - normXAdjusted), 0.6) * 0.82 + 0.18) * vFade;
            const colorObj = colorSpectrum[Math.floor(Math.random() * colorSpectrum.length)];

            pixels.push({
              x,
              y,
              baseAlpha,
              color: colorObj,
              twinklePhase: Math.random() * Math.PI * 2,
              twinkleSpeed: Math.random() * 0.03 + 0.015,
              waveOffset: (side === "left" ? c * 0.28 : (currentCols - c) * 0.28) + (r - currentRows / 2) * 0.1,
              flarePhase: Math.random() * 500,
              flareSpeed: Math.random() * 0.018 + 0.008,
            });
          }
        }
      }
    };

    initGrid();

    let time = 0;

    const render = () => {
      time += 0.032;
      ctx.clearRect(0, 0, width, height);

      pixels.forEach((p) => {
        p.twinklePhase += p.twinkleSpeed;
        p.flarePhase += p.flareSpeed;

        // 1. Organic Kinetic Light Waves flowing across the matrix
        const energyWave = Math.sin(time * 1.6 - p.waveOffset);
        const energyBoost = Math.max(0, energyWave) * 0.42;

        // 2. Individual Twinkle oscillation
        const twinkle = 0.5 + 0.5 * Math.sin(p.twinklePhase);

        // 3. High-energy Star Flare bursts
        const flarePulse = Math.sin(p.flarePhase);
        const isFlaring = flarePulse > 0.93;
        const flareExtra = isFlaring ? (flarePulse - 0.93) * 14 : 0;

        let alpha = p.baseAlpha * (0.32 + 0.48 * twinkle + energyBoost) + flareExtra * 0.35;
        alpha = Math.min(1, Math.max(0.06, alpha));

        const { r, g, b } = p.color;

        if (isFlaring || energyBoost > 0.32) {
          ctx.shadowBlur = isFlaring ? 12 : 7;
          ctx.shadowColor = `rgba(255, 215, 100, ${(alpha * 0.9).toFixed(2)})`;
          ctx.fillStyle = `rgba(${Math.min(255, r + 25)}, ${Math.min(255, g + 25)}, ${Math.min(255, b + 25)}, ${alpha.toFixed(3)})`;
        } else {
          ctx.shadowBlur = 0;
          ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(3)})`;
        }

        if (ctx.roundRect) {
          ctx.beginPath();
          ctx.roundRect(p.x, p.y, cellSize, cellSize, 1.2);
          ctx.fill();
        } else {
          ctx.fillRect(p.x, p.y, cellSize, cellSize);
        }
      });

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, [side]);

  return <canvas ref={canvasRef} className="straight-golden-matrix-canvas" />;
}

const STEPS_DATA = [
  {
    stepNumber: "01",
    icon: "/images/straight_img/usdt_step_vector.png",
    title: "Get verified in minutes",
    description: "Automated KYC with your Emirates ID and a selfie. Most accounts are approved in [X] minutes.",
  },
  {
    stepNumber: "02",
    icon: "/images/straight_img/usdt_step_vector2.png",
    title: "Link your bank and get your IBAN",
    description: "Automated KYC with your Emirates ID and a selfie. Most accounts are approved in [X] minutes.",
  },
  {
    stepNumber: "03",
    icon: "/images/straight_img/usdt_step_vector3.png",
    title: "Deposit AED, free",
    description: "Transfer AED to your IBAN like paying any other payee. There's no deposit fee, and it's credited to your balance automatically.",
  },
  {
    stepNumber: "04",
    icon: "/images/straight_img/usdt_step_vector4.png",
    title: "Convert AED to USDT",
    description: "Buy USDT at the live market price. Hold it on AGCX or withdraw it to your own wallet.",
  },
];

const FEATURES_DATA_TOP = [
  {
    title: "Free AED deposits",
    description: "No deposit fee and no card charges. What you send is what lands in your balance.",
    image: "/images/straight_img/convert_vector.png",
    alt: "Free AED deposits safe box",
  },
  {
    title: "No reference codes",
    description: "Your IBAN recognises your deposits, so there's no reference to type and nothing to get wrong.",
    image: "/images/straight_img/convert_vector2.png",
    alt: "Dedicated IBAN with no reference code",
  },
  {
    title: "Your money, kept apart from ours",
    description: "Your AED is held in a client account at our partner bank, separate from AGCX company accounts. Your balance is never mixed with company funds.",
    image: "/images/straight_img/convert_vector3.png",
    alt: "Segregated fiat funds",
  },
];

const FEATURES_DATA_MID = [
  {
    title: "Secure custody",
    description: "Your crypto is held by an independent, UAE- approved third-party custodian, with 90% of it offline in cold storage. Every deposit and withdrawal has a full audit trail.",
    image: "/images/straight_img/convert_vector4.png",
    alt: "Institutional MPC crypto custody",
  },
  {
    title: "Fully licensed and compliant",
    description: "Licensed by the UAE Capital Market Authority (SCA) and compliant with UAE anti-money-laundering rules and international standards for crypto transfers.",
    image: "/images/straight_img/convert_vector5.png",
    alt: "UAE regulatory compliance",
  },
];

const FEATURE_DATA_WIDE = {
  title: "Cash out when you want",
  description: "Cash out your crypto whenever you want with a fast, simple, and secure experience designed to give you complete control over your funds. Convert your digital assets into cash with ease, access your money when you need it, and move from crypto to cash without unnecessary steps, complicated processes, or hidden friction. Whether you’re taking profits, managing everyday expenses, or simply need liquidity, enjoy a smooth and reliable cash-out experience from start to finish.",
  image: "/images/straight_img/convert_vector6.png",
  alt: "Instant AED cash out",
};

const COSTS_DATA = [
  {
    type: "AED deposit",
    typeSub: "(bank transfer)",
    costBadge: "Free",
    costSub: null,
    costBadgeClass: "straight-badge-green",
    speedMain: "Within 15 minutes",
    speedSub: "during banking hours",
  },
  {
    type: "AED withdrawal",
    typeSub: "(bank transfer)",
    costBadge: "$5 – $15",
    costSub: null,
    costBadgeClass: "straight-badge-gold",
    speedMain: "Same day",
    speedSub: "if requested before 2:00 PM GST",
  },
  {
    type: "Crypto deposit",
    typeSub: "(USDT and others)",
    costBadge: "Free",
    costSub: null,
    costBadgeClass: "straight-badge-blue",
    speedMain: "After 3 network confirmations",
    speedSub: null,
  },
  {
    type: "Crypto withdrawal",
    typeSub: null,
    costBadge: "Network fee",
    costSub: "(varies by asset & network)",
    costBadgeClass: "straight-badge-purple",
    speedMain: "Typically 10–30 minutes",
    speedSub: null,
  },
];

const FAQ_DATA = [
  {
    id: 1,
    question: "1. How do I convert AED to USDT on AGCX?",
    answer: "Sign up and complete quick Emirates ID verification, link your UAE bank account to receive your dedicated IBAN, deposit AED instantly via CBUAE Instant Payments with zero fees, and navigate to the Convert page to swap AED to USDT at live market rates.",
  },
  {
    id: 2,
    question: "2. Is the AED deposit really free?",
    answer: "Yes. AGCX charges 0% fees for AED deposits via UAE domestic bank transfers and the CBUAE Instant Payment Platform. What you transfer from your bank is the exact amount credited to your AGCX account.",
  },
  {
    id: 3,
    question: "3. How long does verification take?",
    answer: "Most UAE residents complete verification in just a few minutes using their Emirates ID and digital selfie verification. Once submitted, accounts are typically approved almost instantly.",
  },
  {
    id: 4,
    question: "4. How long does an AED deposit take?",
    answer: "AED deposits sent via the CBUAE Instant Payment Platform are processed in real time and usually reflect in your AGCX account within seconds to a few minutes, 24/7.",
  },
  {
    id: 5,
    question: "5. What are the deposit and withdrawal limits?",
    answer: "Standard verified individual accounts enjoy flexible daily and monthly deposit and withdrawal limits suitable for both retail and active traders. High-volume traders can request VIP tier upgrades for higher limits.",
  },
  {
    id: 6,
    question: "6. Is my money kept separate from AGCX?",
    answer: "Yes. All client fiat funds are held in segregated, bankruptcy-remote accounts with leading Tier-1 regulated UAE partner banks, strictly separated from AGCX operational company capital.",
  },
  {
    id: 7,
    question: "7. Can I withdraw my USDT to my own wallet?",
    answer: "Absolutely. You can withdraw your converted USDT to any compatible self-custody wallet, hardware wallet, or external exchange across supported blockchain networks anytime.",
  },
  {
    id: 8,
    question: "8. What happens to my money if something goes wrong at AGCX?",
    answer: "Because client fiat funds are segregated in regulated UAE partner banks and crypto assets are stored with institutional multi-party computation (MPC) cold custody, your assets remain protected and recoverable in accordance with UAE regulatory guidelines.",
  },
];

export default function StraightPage() {
  const [activeFaq, setActiveFaq] = useState(null);

  const toggleFaq = (id) => {
    setActiveFaq((prev) => (prev === id ? null : id));
  };

  const scrollToHowItWorks = () => {
    const el = document.getElementById("how-it-works-section");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="straight-page-wrapper">
      <Helmet>
        <title>AED to USDT, straight from your UAE bank | AGCX</title>
        <meta
          name="description"
          content="Get verified in minutes, deposit AED for free, and convert AED to USDT on a licensed UAE exchange with dedicated IBANs and zero deposit fees."
        />
      </Helmet>

      {/* ==========================================================================
          1. HERO SECTION
          ========================================================================== */}
      <section className="straight-hero-section">
        <StraightHeroParticles />
        <div className="straight-hero-bg-overlay"></div>
        <div className="straight-hero-glow-arc"></div>

        <div className="straight-container">
          <div className="straight-hero-content">
            <h1 className="straight-hero-title">
              AED to USDT,
              <br />
              straight from your <span className="straight-text-gold">UAE bank.</span>
            </h1>

            <p className="straight-hero-subtitle">
              Get verified in minutes, deposit AED for free, and convert AED to USDT on a licensed UAE exchange.
            </p>

            <div className="straight-hero-actions">
              <Link to="/signup" className="straight-btn-primary" id="straight-hero-signup-btn">
                <span>Get started free</span>
                <i class="ri-arrow-right-line"></i>
              </Link>
              <button
                type="button"
                onClick={scrollToHowItWorks}
                className="straight-btn-secondary"
                id="straight-hero-how-it-works-btn"
              >
                See how it works
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ==========================================================================
          2. FOUR STEPS SECTION
          ========================================================================== */}
      <section className="straight-steps-section" id="how-it-works-section">

        <div className="steps_afterlft effectusdt">
          <StepsGoldenMatrix side="left" />
        </div>

        <div className="steps_afterrgt effectusdt">
          <StepsGoldenMatrix side="right" />
        </div>

        <div className="straight-container">
          <div className="straight-steps-section-header">
            <h2 className="straight-steps-title">From sign-up to USDT in four steps.</h2>
          </div>

          <div className="straight-steps-grid-container">
            {/* Central Horizontal Blue Dashed Connector Line */}


            <div className="straight-steps-grid">
              {STEPS_DATA.map((step, index) => (
                <div className="straight-step-card-wrapper" key={index}>
                  {/* Outer Orbit SVG Track matching exact Figma layout with generous card spacing */}
                  <svg className="straight-step-orbit-figma-svg" viewBox="0 0 340 580">
                    <defs>
                      <filter id={`goldGlow-${index}`} x="-20%" y="-20%" width="140%" height="140%">
                        <feDropShadow dx="0" dy="0" stdDeviation="3" floodColor="#D1AA67" floodOpacity="0.75" />
                      </filter>
                    </defs>



                    {/* TOP ORBIT ARC: Sweeps up from right blue line node, over the top head, ending cleanly at upper-left orbital node */}
                    {/* Base subtle guide line */}
                    <path
                      d="M 312,290 L 312,170 A 142,142 0 0,0 56.6,84.5"
                      fill="none"
                      strokeWidth="1.2"
                      className="straight-step-base-arc"
                    />
                    {/* Animated Flowing Gold Stroke */}
                    <path
                      d="M 312,290 L 312,170 A 142,142 0 0,0 56.6,84.5"
                      fill="none"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      className="straight-step-top-arc"
                      filter={`url(#goldGlow-${index})`}
                    />

                    {/* BOTTOM ORBIT ARC: Sweeps down from mid-left node, under the bottom base, ending cleanly at lower-right orbital node */}
                    {/* Base subtle guide line */}
                    <path
                      d="M 28,330 L 28,410 A 142,142 0 0,0 283.4,495.5"
                      fill="none"
                      strokeWidth="1.2"
                      className="straight-step-base-arc"
                    />
                    {/* Animated Flowing Gold Stroke (Opposite Direction) */}
                    <path
                      d="M 28,330 L 28,410 A 142,142 0 0,0 283.4,495.5"
                      fill="none"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      className="straight-step-bottom-arc"
                      filter={`url(#goldGlow-${index})`}
                    />

                    {/* STATIC ANCHOR NODES FROM FIGMA (Exact concentric alignment at arc endpoints) */}
                    {/* NODE 1: Upper-Left Terminal Node at (56.6, 84.5) */}
                    <g className="straight-step-node-glow">
                      <circle cx="56.6" cy="84.5" r="7.5" className="straight-step-node-bg" stroke="#D1AA67" strokeWidth="1.5" />
                      <circle cx="56.6" cy="84.5" r="2.8" className="straight-step-node-dot" fill="#D1AA67" />
                    </g>

                    {/* NODE 2: Right Blue-Line Node at (312, 290) */}
                    <g className="straight-step-node-glow">
                      <circle cx="312" cy="290" r="7.5" className="straight-step-node-bg" stroke="#D1AA67" strokeWidth="1.5" />
                      <circle cx="312" cy="290" r="2.8" className="straight-step-node-dot" fill="#D1AA67" />
                    </g>

                    {/* NODE 3: Mid-Left Node at (28, 330) */}
                    <g className="straight-step-node-glow">
                      <circle cx="28" cy="330" r="7.5" className="straight-step-node-bg" stroke="#D1AA67" strokeWidth="1.5" />
                      <circle cx="28" cy="330" r="2.8" className="straight-step-node-dot" fill="#D1AA67" />
                    </g>

                    {/* NODE 4: Lower-Right Terminal Node at (283.4, 495.5) */}
                    <g className="straight-step-node-glow">
                      <circle cx="283.4" cy="495.5" r="7.5" className="straight-step-node-bg" stroke="#D1AA67" strokeWidth="1.5" />
                      <circle cx="283.4" cy="495.5" r="2.8" className="straight-step-node-dot" fill="#D1AA67" />
                    </g>
                  </svg>

                  {/* Main Stadium Box */}
                  <div className="straight-step-card" id={`straight-step-card-${index + 1}`}>
                    <div className="straight-step-icon-box">
                      <img src={step.icon} alt={step.title} className="straight-step-icon" />
                    </div>
                    <h3 className="straight-step-card-title">{step.title}</h3>
                    <p className="straight-step-card-desc">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ==========================================================================
          3. THE SIMPLE WAY TO CONVERT SECTION (FEATURES GRID)
          ========================================================================== */}
      <section className="straight-features-section">
        <div className="straight-features-bg-mesh"></div>

        <div className="straight-container">
          <div className="straight-features-header">
            <h2 className="straight-features-title">
              The simple way to
              <br />
              convert <span className="straight-text-gold">AED to USDT.</span>
            </h2>
          </div>

          {/* Top Row: 3 Cards */}
          <div className="straight-features-grid-top">
            {FEATURES_DATA_TOP.map((feature, index) => (
              <div className="straight-feature-card" key={index} id={`straight-feature-top-${index + 1}`}>
                <div className="straight-feature-card-content">
                  <h3 className="straight-feature-card-title">{feature.title}</h3>
                  <p className="straight-feature-card-desc">{feature.description}</p>
                </div>
                <div className="straight-feature-card-img-wrapper">
                  <img src={feature.image} alt={feature.alt} className="straight-feature-card-img" />
                </div>
              </div>
            ))}
          </div>

          {/* Mid Row: 2 Cards */}
          <div className="straight-features-grid-mid">
            {FEATURES_DATA_MID.map((feature, index) => (
              <div className="straight-feature-card" key={index} id={`straight-feature-mid-${index + 1}`}>
                <div className="straight-feature-card-content">
                  <h3 className="straight-feature-card-title">{feature.title}</h3>
                  <p className="straight-feature-card-desc">{feature.description}</p>
                </div>
                <div className="straight-feature-card-img-wrapper">
                  <img src={feature.image} alt={feature.alt} className="straight-feature-card-img" />
                </div>
              </div>
            ))}
          </div>

          {/* Bottom Row: 1 Wide Card */}
          <div className="straight-features-grid-bottom">
            <div className="straight-feature-card straight-feature-card-wide" id="straight-feature-bottom-wide">
              <div className="straight-feature-card-content">
                <h3 className="straight-feature-card-title">{FEATURE_DATA_WIDE.title}</h3>
                <p className="straight-feature-card-desc">{FEATURE_DATA_WIDE.description}</p>
              </div>
              <div className="straight-feature-card-img-wrapper">
                <img
                  src={FEATURE_DATA_WIDE.image}
                  alt={FEATURE_DATA_WIDE.alt}
                  className="straight-feature-card-img"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==========================================================================
          4. COSTS AND DURATION SECTION (TABLE)
          ========================================================================== */}
      <section className="straight-costs-section">
        <div className="straight-container">
          <div className="straight-costs-header">
            <h2 className="straight-costs-title">
              What it costs, and how
              <br />
              long it takes.
            </h2>
          </div>

          <div className="straight-costs-table-card">
            <div className="straight-costs-table-header-row">
              <div className="straight-costs-th straight-costs-th-type">Type</div>
              <div className="straight-costs-th straight-costs-th-cost">Cost</div>
              <div className="straight-costs-th straight-costs-th-speed">Speed</div>
            </div>

            <div className="straight-costs-rows-list">
              {COSTS_DATA.map((row, index) => (
                <div className="straight-costs-row-card" key={index}>
                  <div className="straight-costs-col straight-costs-col-type">
                    <div className="straight-costs-type-title">{row.type}</div>
                    {row.typeSub && <div className="straight-costs-type-sub">{row.typeSub}</div>}
                  </div>
                  <div className="straight-costs-col straight-costs-col-cost">
                    <div className={`straight-badge ${row.costBadgeClass}`}>
                      <span className="straight-badge-text">{row.costBadge}</span>
                      {row.costSub && <span className="straight-badge-sub">{row.costSub}</span>}
                    </div>
                  </div>
                  <div className="straight-costs-col straight-costs-col-speed">
                    <span className="straight-costs-speed-main">{row.speedMain}</span>
                    {row.speedSub && <span className="straight-costs-speed-sub"> {row.speedSub}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ==========================================================================
          5. FAQ SECTION (ACCORDION)
          ========================================================================== */}
      <section className="straight-faq-section">
        <div className="straight-container">
          <div className="straight-faq-header">
            <h2 className="straight-faq-title">FAQ</h2>
          </div>

          <div className="straight-faq-list">
            {FAQ_DATA.map((faq) => {
              const isOpen = activeFaq === faq.id;
              return (
                <div
                  key={faq.id}
                  className={`straight-faq-item ${isOpen ? "active" : ""}`}
                  id={`straight-faq-item-${faq.id}`}
                >
                  <button
                    type="button"
                    className="straight-faq-question-btn"
                    onClick={() => toggleFaq(faq.id)}
                    aria-expanded={isOpen}
                  >
                    <span>{faq.question}</span>
                    <span className="straight-faq-icon-wrapper">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                    </span>
                  </button>

                  {isOpen && (
                    <div className="straight-faq-answer">
                      <p>{faq.answer}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ==========================================================================
          6. CTA BANNER SECTION
          ========================================================================== */}
      <section className="straight-cta-section">
        <div className="straight-container">
          <div className="straight-cta-banner-card">
            <h2 className="straight-cta-title">Convert AED to USDT today.</h2>
            <p className="straight-cta-subtitle">
              Get verified in minutes, deposit AED for free. Trade on a licensed UAE exchange.
            </p>

            <div className="straight-cta-btn">
              <Link to="/signup" className="straight-btn-primary" id="straight-cta-signup-btn">
                <span>Get started free</span>
              </Link>
            </div>

            <p className="straight-cta-disclaimer">
              Check that converting meets your regulatory requirements, and trade responsibly.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
